"use client";

import React, { useEffect } from 'react';
import styles from './ArticleCard.module.scss';
import Image from 'next/image';
import { ArticleCardProps } from './ArticleCard.interface';
import { formatDate } from "@helpers/dateFormatter";
import { useMediaQuery } from "@hooks/use-media-query";
import Button from '@components/atoms/Button';
import { useGlobalLoader } from '@hooks/useGlobalLoader';
import { useRouter, usePathname } from 'next/navigation';
import { PAGE_TYPES } from '@constants/pageTypes';
import { truncateText } from '@helpers/truncateText';


const ArticleCard: React.FC<ArticleCardProps> = ({
  imageUrl,
  title,
  authorName,
  authorDate,
  description,
  readMoreLink,
  readMoreText,
  cityName
}) => {

  const router = useRouter();
  const pathname = usePathname();
  const { showLoader, hideLoader } = useGlobalLoader();

  useEffect(() => {
    hideLoader();
  }, [pathname, hideLoader]);

  const isSmallDevice = useMediaQuery('(max-height: 700px)');
  const isVerySmallDevice = useMediaQuery('(max-height: 568px)');
  const isShortScreen = useMediaQuery('(max-height: 600px)');
  
  const getTruncationLength = () => {
    if (isVerySmallDevice) return 20;
    if (isShortScreen) return 30;
    if (isSmallDevice) return 30;
    return 80;
  };

  const truncationLength = getTruncationLength();
  const truncatedDescription = truncateText(description || '', truncationLength);

  const handleArticleClick = () => {
    showLoader('Loading...');
    router.push(`${process.env.NEXT_PUBLIC_FRONTEND_HOST}${readMoreLink}`)
  }

  return (
    <div className={styles.articleCard}>
      {imageUrl && (
        <div className={styles.imageContainer}>
          <Image
            src={imageUrl}
            alt={title || 'Article image'}
            fill
            className={styles.articleImage}
            loading='lazy'
            priority={false}
            quality={10}
            sizes="(max-width: 480px) 280px, (max-width: 768px) 320px, 400px"
          />
        </div>
      )}
      <div className={styles.content}>
        <div className={styles.textContainer}>
          <h2 className={styles.title}>{title}</h2>
          <p className={styles.authorDate}>
            {authorName && `${authorName} -`} {formatDate(authorDate)}
          </p>
        </div>
        {truncatedDescription && (
          <p className={styles.description}>{truncatedDescription}</p>
        )}
      </div>
      {readMoreLink && readMoreText && (
        <Button 
          onButtonClick={handleArticleClick}
          className={styles.readMoreButton}
          segmentEventProps={{
            eventName: "action",
            pageType: PAGE_TYPES.HOME_PAGE,
            eventProperties: {
              action_type: "tap_btn",
              action_name: "tap_btn_article",
              feed_title: title,
              article_url: readMoreLink,
              city_name: cityName,

            },
          }}
        >
          {readMoreText}
        </Button>
      )}
    </div>
  );
};

export default ArticleCard; 